import {Injectable, Injector} from '@angular/core';

import {filter, switchMap} from 'rxjs/operators';
import {Action, select, Store} from '@ngrx/store';

import {BpmnViewerService} from '../../services/bpmn-viewer.service';
import {selectGetBpmnXmlParsingStatus, selectProcessExecutionStatus} from '../selectors';
import {
  UpdateExecutionHistoryParsingStatusAction,
  UpdateExecutionPathAction,
  UpdateLatestSelectedNodeIdAction
} from '../actions';
import {ProcessExecutionStatus} from '@ispm-shared/modules/bpmn';
import {BP_Status, State} from '../state';

import {Effect} from '@ngrx/effects';

import {Observable} from 'rxjs/Observable';
import {combineLatest} from 'rxjs/observable/combineLatest';
import {from} from 'rxjs';
import {InjectionNames} from '@ispm/shared/bpmn-io/bpmn-js';

import {Shape} from 'diagram-js/lib/model';
import {ExecutionPath} from '../../models';
import {UpdateSelectedTreatment} from '../../store/actions/treatment-selection.actions';
import {ExecutedPathUtils} from '@ispm-client/modules/process-executor/services';
import {ModelingUtil} from '@ispm/shared/bpmn-io/modeling-util';
import {TestBarEnableConfirmButtonForJobAction} from '@ispm/shared/store/actions/test-bar.actions';
import {NodeType} from '@ispm/shared/bpmn-io/node-type';
import {pathwaySelectionJobPurpose} from '@ispm-client/modules/pathways/models/constants';

@Injectable()
export class ExecutedPathEffects {

  private processExecutionStatus$: Observable<ProcessExecutionStatus> = this.store$.pipe(select(selectProcessExecutionStatus));
  private bpmnXmlParsingStatus$: Observable<BP_Status> = this.store$.pipe(select(selectGetBpmnXmlParsingStatus));
  private combinedObservable = combineLatest(this.processExecutionStatus$, this.bpmnXmlParsingStatus$);

  constructor(private injector: Injector,
              private store$: Store<State>) {}

  private getBpmnViewerService(): BpmnViewerService{
    return this.injector.get(BpmnViewerService);
  }

  @Effect()
  updateExecutedPathEffect$ = this.combinedObservable.pipe(
    filter(([processExecutionStatus, bpmnXmlParsingStatus,]) => processExecutionStatus && bpmnXmlParsingStatus === BP_Status.Completed),
    switchMap(([processExecutionStatus,]) =>
      this.updateExecutedPath(processExecutionStatus)
    )
  );

  updateExecutedPath(processExecutionStatus: ProcessExecutionStatus): Observable<Action> {
    const executionPath = new ExecutionPath();
    const elementRegistry = this.getBpmnViewerService().getBpmnViewer().get(InjectionNames.elementRegistry);
    const executedElements: Shape[] = processExecutionStatus.executedActivities.map(activity => elementRegistry.get(activity.id));
    const currentUserTaskId = processExecutionStatus.currentUserTaskId;
    let currentTaskId = null;
    let currentSubProcessShape = null;
    if (currentUserTaskId) {
      currentTaskId = processExecutionStatus.currentActivitiesHierarchyDeepestToUp[0].id;
      currentSubProcessShape = elementRegistry.get(processExecutionStatus.currentActivitiesHierarchyDeepestToUp[1].id);
      ExecutedPathUtils.setElementsForSelection(
        executionPath,
        currentUserTaskId,
        elementRegistry.get(currentTaskId),
        currentSubProcessShape);
    }
    const executedTreatment = this.getExecutedTreatment(executedElements);
    executionPath.executedTreatmentTaskId = executedTreatment ? executedTreatment.id : null;
    this.setExecutedElements(executionPath, executedElements, currentSubProcessShape, executedTreatment);
    executionPath.userSelectedElement = processExecutionStatus.userSelectedElement;


    executionPath.executedNodes = this.filterDuplicateNodeId(executionPath.executedNodes );

    const actions = [
      new UpdateExecutionPathAction({ executionPath: executionPath }),
      new UpdateExecutionHistoryParsingStatusAction({ status: BP_Status.Completed}),
      new UpdateSelectedTreatment({selectedTreatmentActivityId: executionPath.executedTreatmentTaskId}),
      ...(executionPath.executedTreatmentTaskId ?
        [] :
        [
          new TestBarEnableConfirmButtonForJobAction({enable: false, jobPurpose: pathwaySelectionJobPurpose})
        ]),
      ...(executionPath.currentUserTaskHolder ?
          [
            new UpdateLatestSelectedNodeIdAction({ id: executionPath.currentUserTaskHolder.nodeId})
          ] : (executionPath.userSelectedElement ? [
            new UpdateLatestSelectedNodeIdAction({ id: executionPath.userSelectedElement})
          ] : []))
    ];
    return from(actions);
  }

  private filterDuplicateNodeId(executedNodes?: Node[] | any) {
    const uniqueExecutedNodesSet = new Set();
    if(Array.isArray(executedNodes)) {
      return executedNodes.filter((node) => {
        const isPresentInSet = uniqueExecutedNodesSet.has(node);
        uniqueExecutedNodesSet.add(node);
        return !isPresentInSet;
      });
    }
    return executedNodes;
  }

  private isShapeDisplayableInPathway(shape: Shape) {
    switch (ModelingUtil.getNodeType(shape)) {
      case NodeType.normal:
      case NodeType.head:
      case NodeType.neighborhood_entry:
      case NodeType.neighborhood_head:
        return true;
      default:
        return false;
    }
  }

  private setExecutedElements(
    executionPath: ExecutionPath,
    executedElements: Shape[],
    currentSubProcessShape: Shape,
    executedTreatment: Shape) {

    const executedNodeShapes: Shape[] = executedElements.filter(this.isShapeDisplayableInPathway);
    executionPath.executedNodes = ExecutedPathUtils.convertShapesToNodes(executedNodeShapes);
    const currentSubProcessShapeArr = currentSubProcessShape ? [currentSubProcessShape] : [];
    const nodesForExecutedConnectionsCalc: Shape[] = executedNodeShapes.concat(currentSubProcessShapeArr);

    for (let index = 0; index < nodesForExecutedConnectionsCalc.length - 1; index++) {
      const shapesBetweenNodes = ExecutedPathUtils
        .getShapesFromParentToAChild(nodesForExecutedConnectionsCalc[index], nodesForExecutedConnectionsCalc[index + 1]);
      executionPath.executedConnectionsIds.push(shapesBetweenNodes.connectionFromParentToGateway.id);
      executionPath.executedConnectionsIds.push(shapesBetweenNodes.connectionsFromGatewayToAChild.id);
      executionPath.executedGatewaysIds.push(shapesBetweenNodes.gateway.id);
    }

    if (executedTreatment) {
      const intersection = ModelingUtil.getConnectionIdsFromParentToLeaf(nodesForExecutedConnectionsCalc.pop(), executedTreatment);
      if (intersection) {
        executionPath.executedConnectionsIds.push(intersection[0]);
      }
    }
  }

  private getExecutedTreatment(executedElements: Shape[]) {
    if (executedElements) {
      return executedElements.find(element => ModelingUtil.getNodeType(element) === NodeType.treatment);
    }
  }
}
